import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { 
  Building2, Plus, Trash2, ExternalLink, Loader2, GripVertical, Edit, Globe
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "title_sponsor", name: "Title Sponsor", color: "bg-yellow-500" },
  { id: "gold_sponsor", name: "Gold Sponsors", color: "bg-yellow-400" },
  { id: "silver_sponsor", name: "Silver Sponsors", color: "bg-gray-400" },
  { id: "partner", name: "Partners", color: "bg-blue-500" },
  { id: "government", name: "Government Partners", color: "bg-green-500" },
  { id: "media", name: "Media Partners", color: "bg-purple-500" }
];

const AdminPartners = () => {
  const { getAuthAxios } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newPartner, setNewPartner] = useState({
    name: "",
    logo_url: "",
    website_url: "",
    category: "partner",
    display_order: 0
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data.partners || []);
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPartner = async () => {
    if (!newPartner.name || !newPartner.logo_url) {
      toast.error("Please enter name and logo URL");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/partners`, newPartner);
      toast.success("Partner added successfully!");
      setNewPartner({
        name: "",
        logo_url: "",
        website_url: "",
        category: "partner",
        display_order: 0
      });
      setAddDialogOpen(false);
      fetchPartners();
    } catch (error) {
      toast.error("Failed to add partner");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm("Are you sure you want to delete this partner?")) return;

    try {
      const authAxios = getAuthAxios();
      await authAxios.delete(`${API}/partners/${partnerId}`);
      toast.success("Partner deleted!");
      fetchPartners();
    } catch (error) {
      toast.error("Failed to delete partner");
    }
  };

  // Group partners by category
  const groupedPartners = CATEGORIES.map(cat => ({
    ...cat,
    partners: partners.filter(p => p.category === cat.id)
  })).filter(cat => cat.partners.length > 0);

  return (
    <div data-testid="admin-partners">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Partners & Sponsors</h1>
          <p className="text-[#4B5563]">Manage sponsor and partner logos displayed on the website</p>
        </div>
        
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-partner-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Partner
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Partner / Sponsor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Partner Name *</Label>
                <Input 
                  placeholder="e.g., Decathlon, Yakult, Government of Telangana"
                  value={newPartner.name}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={newPartner.category} 
                  onValueChange={(v) => setNewPartner(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Logo URL *</Label>
                <Input 
                  placeholder="https://example.com/logo.png"
                  value={newPartner.logo_url}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, logo_url: e.target.value }))}
                />
                <p className="text-xs text-[#4B5563]">
                  Upload logo image here (drag & drop in chat) and paste the URL
                </p>
                
                {/* URL validation warning */}
                {newPartner.logo_url && !newPartner.logo_url.match(/\.(jpg|jpeg|png|gif|webp|svg)($|\?)/i) && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                    <p className="text-xs text-yellow-800">
                      ⚠️ Make sure this is a direct image URL ending in .png, .jpg, etc.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Website URL (optional)</Label>
                <Input 
                  placeholder="https://company-website.com"
                  value={newPartner.website_url}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, website_url: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  placeholder="0"
                  value={newPartner.display_order}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                />
                <p className="text-xs text-[#4B5563]">Lower numbers appear first</p>
              </div>
              
              {/* Logo Preview */}
              {newPartner.logo_url && (
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-2">Preview:</p>
                  <img 
                    src={newPartner.logo_url} 
                    alt="Logo preview" 
                    className="h-16 w-auto object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
              
              <Button 
                onClick={handleAddPartner} 
                className="w-full btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Partner
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-4 mb-8">
        <div className="bg-white rounded-xl px-6 py-4 shadow-sm border flex items-center gap-3">
          <Building2 className="w-6 h-6 text-[#FF3B30]" />
          <div>
            <p className="text-2xl font-bold text-[#0A0A0A]">{partners.length}</p>
            <p className="text-sm text-[#4B5563]">Total Partners</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF3B30]" />
          <p className="mt-2 text-[#4B5563]">Loading partners...</p>
        </div>
      ) : partners.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">No Partners Added</h3>
            <p className="text-[#4B5563] mb-4">
              Add sponsor and partner logos to display on the website
            </p>
            <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add First Partner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {groupedPartners.map((category) => (
            <Card key={category.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ${category.color}`} />
                  {category.name}
                  <span className="text-sm font-normal text-[#4B5563]">
                    ({category.partners.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {category.partners.map((partner) => (
                    <div 
                      key={partner.id} 
                      className="relative group bg-gray-50 rounded-xl p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="aspect-[3/2] flex items-center justify-center mb-2">
                        <img 
                          src={partner.logo_url}
                          alt={partner.name}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                      <p className="text-sm font-medium text-[#0A0A0A] text-center truncate">
                        {partner.name}
                      </p>
                      
                      {/* Actions overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                        {partner.website_url && (
                          <a 
                            href={partner.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white rounded-full hover:bg-gray-100"
                          >
                            <Globe className="w-4 h-4 text-[#007AFF]" />
                          </a>
                        )}
                        <button 
                          onClick={() => handleDeletePartner(partner.id)}
                          className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Instructions */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>How to Add Partner Logos</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3 text-[#4B5563]">
            <li className="flex gap-3">
              <span className="w-6 h-6 bg-[#FF3B30] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
              <span><strong>Upload the logo image</strong> - Drag & drop the logo into the chat window. You'll get a direct URL.</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 bg-[#FF3B30] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
              <span><strong>Copy the URL</strong> - After uploading, copy the provided URL.</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 bg-[#FF3B30] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
              <span><strong>Add Partner</strong> - Click "Add Partner", paste the URL, fill in details, and save.</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPartners;
